import React, { useState, useRef, useCallback, useEffect } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Avatar,
  Chip,
  Divider,
  Paper,
  IconButton,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Download,
  ArrowBack,
  Person,
  Email,
  Phone,
  Description,
  TextSnippet,
  Image as ImageIcon,
  VideoCameraBack,
} from "@mui/icons-material";
import { useGetChatDetailsByIdQuery } from "../../services/tutorChatReport";
import Loader from "../../helpers/Loader";
import moment from "moment";
import { styled } from "@mui/material/styles";

// Define TypeScript interfaces for the API response
interface User {
  _id: string;
  name: string;
  email: string;
  phoneNo: string;
  image?: string;
}

interface ChatMessage {
  _id: string;
  connectionId: string;
  bookingId: string | null;
  parentId: User;
  tutorId: User;
  uploads: string[];
  uploadType: "TEXT" | "DOC" | "IMAGE" | "VIDEO";
  isParentRead: boolean;
  isTutorRead: boolean;
  isTutorBlocked: boolean;
  isParentBlocked: boolean;
  isDeleted: boolean;
  sentBy?: number;
  message?: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

// Styled components using Material-UI
const MessagesContainer = styled(Box)({
  maxHeight: "600px",
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
});

const MessageItem = styled(Paper)({
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
});

const TutorMessage = styled(MessageItem)({
  backgroundColor: "#e3f2fd",
});

const ParentMessage = styled(MessageItem)({
  backgroundColor: "#e8f5e8",
});

const MessageText = styled(Typography)({
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
});

const FileList = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginTop: "8px",
});

const ImagePreview = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginTop: "8px",
});

const PreviewImg = styled("img")({
  width: "100px",
  height: "100px",
  objectFit: "cover",
  borderRadius: "4px",
  cursor: "pointer",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "scale(1.05)",
  },
});

const UserInfo = styled(Box)({
  marginBottom: "16px",
});

const ChatStats = styled(Box)({
  marginTop: "16px",
});

const LoadMoreContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: "16px",
});

const ChatDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { _id } = useParams();
  const { state } = location;

  const [page, setPage] = useState(1);
  const [allMessages, setAllMessages] = useState<ChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const previousScrollHeight = useRef<number>(0);

  const {
    data: chatData,
    isLoading,
    error,
    isFetching,
  } = useGetChatDetailsByIdQuery({ id: _id, page, limit: 20 }, { skip: !_id });

  // Safely extract messages from the API response
  const getMessagesFromResponse = (data: any): ChatMessage[] => {
    if (!data) return [];

    if (Array.isArray(data)) {
      return data;
    } else if (data.message && Array.isArray(data.message)) {
      return data.message;
    } else if (
      data.data &&
      data.data.message &&
      Array.isArray(data.data.message)
    ) {
      return data.data.message;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    }

    return [];
  };

  // Safely extract total messages count
  const getTotalMessages = (data: any): number => {
    if (!data) return 0;

    if (data.totalmessage) return data.totalmessage;
    if (data.data && data.data.totalmessage) return data.data.totalmessage;
    if (Array.isArray(data)) return data.length;
    if (data.message && Array.isArray(data.message)) return data.message.length;

    return 0;
  };

  const currentMessages = getMessagesFromResponse(chatData);
  const totalMessages = getTotalMessages(chatData);

  // Reset state when chat ID changes
  useEffect(() => {
    setAllMessages([]);
    setPage(1);
    setHasMore(true);
    setIsInitialLoad(true);
  }, [_id]);

  // Update allMessages when new data is fetched
  useEffect(() => {
    if (currentMessages.length > 0) {
      // Store scroll position before updating messages
      const container = messagesContainerRef.current;
      if (container && page > 1) {
        previousScrollHeight.current = container.scrollHeight;
      }

      if (page === 1) {
        setAllMessages(currentMessages);
        setIsInitialLoad(false);
      } else {
        // Append new messages to the end (for infinite scroll at bottom)
        const newMessages = currentMessages.filter(
          (newMsg) =>
            !allMessages.some((existingMsg) => existingMsg._id === newMsg._id)
        );
        setAllMessages((prev) => [...prev, ...newMessages]);
      }

      // Check if there are more messages to load
      if (currentMessages.length < 20) {
        setHasMore(false);
      }
    } else if (page > 1) {
      setHasMore(false);
    }
  }, [currentMessages, page]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (page === 1 && allMessages.length > 0 && !isFetching && isInitialLoad) {
      scrollToBottom();
      setIsInitialLoad(false);
    }
  }, [allMessages, page, isFetching, isInitialLoad]);

  // Maintain scroll position when loading more messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container && page > 1 && !isFetching) {
      const newScrollHeight = container.scrollHeight;
      const scrollDifference = newScrollHeight - previousScrollHeight.current;

      if (scrollDifference > 0) {
        container.scrollTop += scrollDifference;
      }
    }
  }, [allMessages, isFetching, page]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || isFetching || !hasMore) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Load more when scrolled near the bottom (within 100px)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMoreMessages();
    }
  }, [isFetching, hasMore]);

  const loadMoreMessages = () => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  // Function to download files
  const downloadFile = (url: string, filename: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to render message content based on type
  const renderMessageContent = (message: ChatMessage) => {
    switch (message.uploadType) {
      case "TEXT":
        return (
          <MessageText variant="body1" className="message-text">
            {message.message || "No text message"}
          </MessageText>
        );

      case "DOC":
        return (
          <Box className="message-doc">
            <Description color="primary" />
            <Typography variant="body2">
              Document ({message.uploads?.length || 0} files)
            </Typography>
            <FileList className="file-list">
              {message.uploads?.map((file: string, index: number) => (
                <Chip
                  key={index}
                  icon={<Download />}
                  label={`Document ${index + 1}`}
                  onClick={() =>
                    downloadFile(file, `document-${index + 1}.pdf`)
                  }
                  variant="outlined"
                  size="small"
                />
              ))}
            </FileList>
          </Box>
        );

      case "IMAGE":
        return (
          <Box className="message-image">
            <ImageIcon color="primary" />
            <Typography variant="body2">
              Image ({message.uploads?.length || 0} files)
            </Typography>
            <ImagePreview className="image-preview">
              {message.uploads?.map((image: string, index: number) => (
                <PreviewImg
                  key={index}
                  src={image}
                  alt={`Image ${index + 1}`}
                  className="preview-img"
                  onClick={() => window.open(image, "_blank")}
                />
              ))}
            </ImagePreview>
          </Box>
        );

      case "VIDEO":
        return (
          <Box className="message-video">
            <VideoCameraBack color="primary" />
            <Typography variant="body2">
              Video ({message.uploads?.length || 0} files)
            </Typography>
            <FileList className="video-list">
              {message.uploads?.map((video: string, index: number) => (
                <Chip
                  key={index}
                  icon={<Download />}
                  label={`Video ${index + 1}`}
                  onClick={() => downloadFile(video, `video-${index + 1}.mp4`)}
                  variant="outlined"
                  size="small"
                />
              ))}
            </FileList>
          </Box>
        );

      default:
        return (
          <Typography variant="body2" color="textSecondary">
            Unknown message type
          </Typography>
        );
    }
  };

  return (
    <MainContainer>
      <Loader isLoad={isLoading} />
      <div className="main_loyout">
        <div className="dashboard">
          <h1>View Chat Details</h1>
          <Button
            className="btn btn_primary"
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/manage-reported`)}
          >
            Back
          </Button>
        </div>

        {error && (
          <Card className="cards" sx={{ mb: 3 }}>
            <CardContent>
              <Typography color="error">
                Error loading chat details: {error.toString()}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Grid container spacing={3}>
          {/* User Information Card */}
          <Grid item xs={12} md={4}>
            <Card className="cards">
              <CardContent>
                <Typography variant="h6" gutterBottom className="subhdng">
                  Chat Participants
                </Typography>

                {/* Parent Information */}
                <UserInfo className="user-info">
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Customer (Parent)
                  </Typography>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ mr: 2 }}>
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {allMessages[0]?.parentId?.name || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <Email
                          fontSize="small"
                          sx={{ fontSize: 16, mr: 0.5 }}
                        />
                        {allMessages[0]?.parentId?.email || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <Phone
                          fontSize="small"
                          sx={{ fontSize: 16, mr: 0.5 }}
                        />
                        {allMessages[0]?.parentId?.phoneNo || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </UserInfo>

                <Divider sx={{ my: 2 }} />

                {/* Tutor Information */}
                <UserInfo className="user-info">
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Tutor
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Avatar src={allMessages[0]?.tutorId?.image} sx={{ mr: 2 }}>
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {allMessages[0]?.tutorId?.name || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <Email
                          fontSize="small"
                          sx={{ fontSize: 16, mr: 0.5 }}
                        />
                        {allMessages[0]?.tutorId?.email || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <Phone
                          fontSize="small"
                          sx={{ fontSize: 16, mr: 0.5 }}
                        />
                        {allMessages[0]?.tutorId?.phoneNo || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </UserInfo>

                <Divider sx={{ my: 2 }} />

                {/* Chat Statistics */}
                <ChatStats className="chat-stats">
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Chat Statistics
                  </Typography>
                  <Typography variant="body2">
                    Total Messages: {totalMessages}
                  </Typography>
                  <Typography variant="body2">
                    Loaded Messages: {allMessages.length}
                  </Typography>
                  <Typography variant="body2">
                    Connection ID: {allMessages[0]?.connectionId || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    Last Updated:{" "}
                    {allMessages[0]
                      ? moment(allMessages[0].updatedAt).format("LLL")
                      : "N/A"}
                  </Typography>
                </ChatStats>
              </CardContent>
            </Card>
          </Grid>

          {/* Messages Card */}
          <Grid item xs={12} md={8}>
            <Card className="cards">
              <CardContent>
                <Typography variant="h6" gutterBottom className="subhdng">
                  Chat Messages ({allMessages.length} of {totalMessages})
                </Typography>

                {allMessages.length === 0 && !isLoading ? (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                    py={4}
                  >
                    No messages found in this chat
                  </Typography>
                ) : (
                  <MessagesContainer
                    className="messages-container"
                    ref={messagesContainerRef}
                    onScroll={handleScroll}
                  >
                    {allMessages.map((message: ChatMessage, index: number) => {
                      const MessageComponent =
                        message.sentBy === 2 ? TutorMessage : ParentMessage;

                      return (
                        <MessageComponent
                          key={message._id || index}
                          elevation={1}
                          sx={{
                            p: 2,
                            mb: 2,
                            borderLeft: `4px solid ${
                              message.sentBy === 2 ? "#1976d2" : "#2e7d32"
                            }`,
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" mb={1}>
                                <Avatar
                                  src={
                                    message.sentBy === 2
                                      ? message.tutorId?.image
                                      : undefined
                                  }
                                  sx={{ width: 32, height: 32, mr: 1 }}
                                >
                                  <Person />
                                </Avatar>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  {message.sentBy === 2
                                    ? message.tutorId?.name
                                    : message.parentId?.name}
                                </Typography>
                                <Chip
                                  label={
                                    message.sentBy === 2 ? "Tutor" : "Parent"
                                  }
                                  size="small"
                                  color={
                                    message.sentBy === 2 ? "primary" : "success"
                                  }
                                  sx={{ ml: 1, height: 20 }}
                                />
                              </Box>

                              {renderMessageContent(message)}

                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                                mt={1}
                              >
                                {moment(message.createdAt).format(
                                  "MMM D, YYYY h:mm A"
                                )}
                              </Typography>
                            </Box>

                            {message.uploads && message.uploads.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  downloadFile(
                                    message.uploads[0],
                                    `file-${index}`
                                  )
                                }
                              >
                                <Download />
                              </IconButton>
                            )}
                          </Box>

                          {/* Message status */}
                          <Box display="flex" justifyContent="flex-end" mt={1}>
                            <Typography variant="caption" color="textSecondary">
                              {message.isParentRead && "Read by Parent • "}
                              {message.isTutorRead && "Read by Tutor • "}
                              {message.uploadType}
                            </Typography>
                          </Box>
                        </MessageComponent>
                      );
                    })}

                    {/* Scroll anchor at the bottom */}
                    <div ref={messagesEndRef} />

                    {/* Load more indicator at the bottom */}
                    {isFetching && (
                      <LoadMoreContainer>
                        <Typography variant="body2" color="textSecondary">
                          Loading more messages...
                        </Typography>
                      </LoadMoreContainer>
                    )}

                    {/* Load more button at the bottom for manual loading */}
                    {hasMore && !isFetching && (
                      <LoadMoreContainer>
                        <Button
                          variant="outlined"
                          onClick={loadMoreMessages}
                          disabled={isFetching}
                        >
                          Load More Messages
                        </Button>
                      </LoadMoreContainer>
                    )}
                  </MessagesContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
    </MainContainer>
  );
};

export default ChatDetails;
